// =============================================================================
// MeuDriver — Batch O4: AdminPayouts
// Painel de gestão de payouts e financeiro para administradores — /admin/pagamentos
// =============================================================================

import { useState } from 'react'
import {
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertCircle,
  ShieldCheck,
  ShieldAlert,
  Loader2,
  Users,
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  useAdminPayoutRequests,
  useAdminApproveMpAccount,
  useAdminFinancialSummary,
  type AdminPayoutRow,
  type PayoutStatusFilter,
} from '@/hooks/useAdminPayouts'
import { formatCurrency } from '@/lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getStatusLabel(status: AdminPayoutRow['status']): string {
  const map: Record<AdminPayoutRow['status'], string> = {
    pending: 'Pendente',
    processing: 'Processando',
    completed: 'Concluído',
    failed: 'Falhou',
  }
  return map[status]
}

function StatusBadge({ status }: { status: AdminPayoutRow['status'] }) {
  const configs: Record<AdminPayoutRow['status'], { className: string }> = {
    pending:    { className: 'bg-amber-50 text-amber-700 border-amber-200' },
    processing: { className: 'bg-blue-50 text-blue-700 border-blue-200' },
    completed:  { className: 'bg-green-50 text-green-700 border-green-200' },
    failed:     { className: 'bg-red-50 text-red-600 border-red-200' },
  }
  return (
    <Badge className={`text-xs border ${configs[status].className}`}>
      {getStatusLabel(status)}
    </Badge>
  )
}

function getInitials(name: string | null): string {
  if (!name) return '?'
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

// ---------------------------------------------------------------------------
// KPI Cards
// ---------------------------------------------------------------------------

interface KpiCardProps {
  label: string
  value: string
  icon: React.ReactNode
  className?: string
}

function KpiCard({ label, value, icon, className = '' }: KpiCardProps) {
  return (
    <Card className={className}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-muted-foreground font-medium">{label}</p>
          <div className="h-9 w-9 rounded-xl bg-brand-50 flex items-center justify-center">
            {icon}
          </div>
        </div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// PayoutRow — linha da tabela de payouts
// ---------------------------------------------------------------------------

interface PayoutTableRowProps {
  row: AdminPayoutRow
  onApproveMp: (instructorId: string) => void
  isApproving: boolean
}

function PayoutTableRow({ row, onApproveMp, isApproving }: PayoutTableRowProps) {
  return (
    <div className="flex items-center gap-4 py-4 border-b border-border/60 last:border-0">
      {/* Avatar + nome */}
      <Avatar className="h-9 w-9 shrink-0">
        <AvatarFallback className="bg-brand-100 text-brand-700 text-sm font-semibold">
          {getInitials(row.instructor_name)}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 truncate">
          {row.instructor_name ?? 'Instrutor'}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {row.instructor_email ?? '—'}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Solicitado em{' '}
          {format(parseISO(row.requested_at), "d 'de' MMM 'de' yyyy", { locale: ptBR })}
        </p>
      </div>

      {/* MP Account status */}
      <div className="hidden sm:flex items-center gap-1.5 shrink-0">
        {row.mp_verified ? (
          <span className="flex items-center gap-1 text-xs text-green-700">
            <ShieldCheck className="h-3.5 w-3.5" />
            Conta MP verificada
          </span>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="text-xs gap-1 text-amber-700 border-amber-300 hover:bg-amber-50"
            onClick={() => onApproveMp(row.instructor_id)}
            disabled={isApproving}
            data-testid={`approve-mp-${row.instructor_id}`}
          >
            {isApproving ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <ShieldAlert className="h-3.5 w-3.5" />
            )}
            Aprovar conta MP
          </Button>
        )}
      </div>

      {/* Valor */}
      <div className="text-right shrink-0">
        <p className="font-bold text-gray-900">{formatCurrency(row.amount)}</p>
        <StatusBadge status={row.status} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// PayoutTab — conteúdo de cada aba
// ---------------------------------------------------------------------------

interface PayoutTabProps {
  statusFilter: PayoutStatusFilter
}

function PayoutTab({ statusFilter }: PayoutTabProps) {
  const { data: rows, isLoading } = useAdminPayoutRequests(statusFilter)
  const approveMp = useAdminApproveMpAccount()

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 rounded-xl" />
        ))}
      </div>
    )
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <CheckCircle2 className="h-10 w-10 text-gray-300" />
        <p className="text-sm text-muted-foreground">Nenhum payout encontrado.</p>
      </div>
    )
  }

  return (
    <div className="divide-y divide-border/40">
      {rows.map((row) => (
        <PayoutTableRow
          key={row.payout_id}
          row={row}
          onApproveMp={(id) => approveMp.mutate({ instructorId: id })}
          isApproving={approveMp.isPending}
        />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// AdminPayouts — página principal
// ---------------------------------------------------------------------------

const TAB_LABELS: { value: PayoutStatusFilter; label: string }[] = [
  { value: undefined,     label: 'Todos' },
  { value: 'pending',     label: 'Pendentes' },
  { value: 'processing',  label: 'Em processamento' },
  { value: 'completed',   label: 'Concluídos' },
  { value: 'failed',      label: 'Falhos' },
]

export default function AdminPayouts() {
  const { data: summary, isLoading: summaryLoading } = useAdminFinancialSummary()
  const [activeTab, setActiveTab] = useState<string>('all')

  const kpis = [
    {
      label: 'GMV total',
      value: summaryLoading ? '...' : formatCurrency(summary?.total_gmv ?? 0),
      icon: <TrendingUp className="h-4 w-4 text-brand-600" />,
    },
    {
      label: 'Comissão plataforma (15%)',
      value: summaryLoading ? '...' : formatCurrency(summary?.total_commission ?? 0),
      icon: <DollarSign className="h-4 w-4 text-brand-600" />,
    },
    {
      label: 'Total repassado',
      value: summaryLoading ? '...' : formatCurrency(summary?.total_paid_out ?? 0),
      icon: <CheckCircle2 className="h-4 w-4 text-green-600" />,
    },
    {
      label: 'Payouts pendentes',
      value: summaryLoading ? '...' : formatCurrency(summary?.pending_payouts ?? 0),
      icon: <Clock className="h-4 w-4 text-amber-600" />,
    },
    {
      label: 'Saldo plataforma',
      value: summaryLoading ? '...' : formatCurrency(summary?.platform_balance ?? 0),
      icon: <Users className="h-4 w-4 text-brand-600" />,
    },
  ]

  const tabStatusMap: Record<string, PayoutStatusFilter> = {
    all:        undefined,
    pending:    'pending',
    processing: 'processing',
    completed:  'completed',
    failed:     'failed',
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
          <DollarSign className="h-6 w-6 text-brand-600" />
          Pagamentos
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Gerencie repasses aos instrutores e acompanhe o financeiro da plataforma.
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {kpis.map((kpi) => (
          <KpiCard
            key={kpi.label}
            label={kpi.label}
            value={kpi.value}
            icon={kpi.icon}
          />
        ))}
      </div>

      {/* Tabela de payouts */}
      <Card>
        <CardHeader className="pb-0">
          <CardTitle className="text-base font-semibold">Histórico de repasses</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4 flex-wrap h-auto gap-1">
              {TAB_LABELS.map(({ value, label }) => (
                <TabsTrigger
                  key={value ?? 'all'}
                  value={value ?? 'all'}
                  data-testid={`payout-tab-${value ?? 'all'}`}
                >
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>

            {TAB_LABELS.map(({ value, label }) => (
              <TabsContent key={value ?? 'all'} value={value ?? 'all'}>
                <PayoutTab statusFilter={tabStatusMap[value ?? 'all']} />
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}
